import {
  CLASSIC_RULESET,
  type Coord,
  lastVolley,
  type Seat,
  type Shot,
  shipCount,
  sunkShipsFromShots,
} from '@sea-wars/game-core';
import { SketchBoard } from '@sea-wars/ui';
import { useEffect, useMemo, useState } from 'react';
import type { GameView } from '../ws/useGame.js';

const TOTAL_SHIPS = shipCount(CLASSIC_RULESET.roster);

/** Każdy statek dostaje dokładnie jeden strzał z wynikiem `sunk` — ten, który go dobija. */
const countSunk = (shots: readonly Shot[]): number =>
  shots.filter((shot) => shot.result === 'sunk').length;

export interface GameBoardsProps {
  readonly view: GameView;
  readonly seed: number;
  /** Komunikat błędu z serwera; jego pojawienie się zwalnia blokadę planszy. */
  readonly error: string | null;
  readonly onFire: (cell: Coord) => void;
}

/** Po tylu milisekundach bez odpowiedzi odblokowujemy planszę mimo wszystko. */
const SHOT_TIMEOUT_MS = 8000;

/**
 * Dwie plansze rozgrywki.
 *
 * Na telefonie plansza przeciwnika idzie pierwsza i na całą szerokość — tam patrzy gracz.
 * Własna zostaje pod nią, jako podgląd tego, co dostaje od przeciwnika (patrz `docs/05`).
 */
export function GameBoards({ view, seed, error, onFire }: GameBoardsProps) {
  const [aim, setAim] = useState<Coord | null>(null);
  const [pendingAt, setPendingAt] = useState<number | null>(null);
  const finished = view.status === 'finished';
  const enemySeat: Seat = view.you === 0 ? 1 : 0;

  /**
   * Po oddaniu strzału plansza jest zablokowana do czasu odpowiedzi serwera.
   *
   * Klient nie wie z góry, czy tura zostanie przy nim: trafienie daje kolejny strzał,
   * pudło oddaje ruch. Pokazywanie w tym czasie „twoja kolej" byłoby kłamstwem
   * i kusiłoby do drugiego kliknięcia, zanim serwer rozstrzygnie pierwsze.
   */
  const shotCount = view.enemyShots.length + view.incoming.length;
  const awaitingServer = pendingAt !== null && shotCount === pendingAt;
  const yourTurn = view.status === 'in_progress' && view.turn === view.you && !awaitingServer;

  // Odrzucona komenda nie wywoła żadnego strzału, więc bez tego blokada zostałaby
  // na zawsze i plansza przestałaby reagować.
  useEffect(() => {
    if (error) setPendingAt(null);
  }, [error]);

  // Zabezpieczenie na wypadek zgubionej ramki: blokada nie może być wieczna.
  useEffect(() => {
    if (!awaitingServer) return;
    const timer = setTimeout(() => setPendingAt(null), SHOT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [awaitingServer]);

  /** Pola już ostrzelane — nie ma po co pytać serwera o coś, co widać na planszy. */
  const spent = useMemo(
    () => new Set(view.enemyShots.map((shot) => `${shot.x}-${shot.y}`)),
    [view.enemyShots],
  );

  /**
   * Zatopione statki przeciwnika w trakcie partii, zacieniowane jak cała flota po jej końcu —
   * odtworzone z samych trafień, więc nic ponad to, co gracz już wie.
   */
  const sunkEnemyShips = useMemo(
    () => sunkShipsFromShots(view.enemyShots, CLASSIC_RULESET.boardSize),
    [view.enemyShots],
  );

  /** Ostatnia seria przeciwnika — oznaczana tylko na mojej planszy, tam gdzie strzela. */
  const opponentLastVolley = useMemo(
    () => lastVolley(view.incoming, CLASSIC_RULESET),
    [view.incoming],
  );

  function fire(cell: Coord) {
    setPendingAt(shotCount);
    setAim(null);
    onFire(cell);
  }

  function tap(cell: Coord) {
    if (!yourTurn || spent.has(`${cell.x}-${cell.y}`)) return;
    // Celowanie dwuetapowe: pierwsze tapnięcie ustawia celownik, drugie strzela.
    // Bez tego przewijanie strony palcem kończyłoby się przypadkowym strzałem.
    if (aim && aim.x === cell.x && aim.y === cell.y) fire(cell);
    else setAim(cell);
  }

  // "Żywe" statki = całość floty minus te, które już dostały ostateczny, kończący strzał.
  const enemyAlive = TOTAL_SHIPS - countSunk(view.enemyShots);
  const ownAlive = TOTAL_SHIPS - countSunk(view.incoming);

  return (
    <div className="app__boards">
      <section className="board-card" aria-labelledby="enemy-heading">
        <div className="board-card__header">
          <h2 id="enemy-heading" className="hand">
            Wróg
          </h2>
          {view.status === 'in_progress' && (
            <span className={`turn-badge${yourTurn ? ' turn-badge--mine' : ''}`}>
              {awaitingServer
                ? 'Strzał w drodze…'
                : !yourTurn
                  ? 'Czekaj'
                  : aim
                    ? 'Strzał'
                    : 'Twoja kolej'}
            </span>
          )}
          <span className="board-card__fleet-count">
            {enemyAlive}/{TOTAL_SHIPS} statków
          </span>
        </div>
        <SketchBoard
          size={CLASSIC_RULESET.boardSize}
          seed={seed + 1}
          shots={view.enemyShots}
          fleet={finished ? (view.fleets?.[enemySeat] ?? null) : sunkEnemyShips}
          aim={aim}
          interactive={view.status === 'in_progress'}
          disabled={!yourTurn}
          dimmed={view.status === 'in_progress' && !yourTurn}
          ariaLabel="Plansza przeciwnika — tutaj strzelasz"
          onCellActivate={tap}
        />
      </section>

      <section className="board-card" aria-labelledby="own-heading">
        <div className="board-card__header">
          <h2 id="own-heading" className="hand">
            Twoja flota
          </h2>
          <span className="board-card__fleet-count">
            {ownAlive}/{TOTAL_SHIPS} statków
          </span>
        </div>
        <SketchBoard
          size={CLASSIC_RULESET.boardSize}
          seed={seed}
          fleet={view.ownFleet}
          shots={view.incoming}
          recent={opponentLastVolley}
          ariaLabel="Twoja plansza — tutaj strzela przeciwnik"
        />
      </section>
    </div>
  );
}
